import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { rawFetch } from "@/lib/api/client";
import { parseApiError } from "@/lib/api/client";
import {
  PageHeader, ErrorBox, StatusPill, EmptyState,
} from "@/components/common";
import { PaginatedTable } from "@/components/PaginatedTable";
import { useApp } from "@/lib/store";
import { useWorkspacePermissions } from "@/lib/workspacePermissions";
import { fmtDate, shortId } from "@/lib/utils";
import { DiscoveryReviewPanel } from "./DiscoveryReviewPanel";
import { DictionaryImporter } from "@/components/DictionaryImporter";
import { CloudDictionaryBrowser } from "@/components/CloudDictionaryBrowser";
import { Cloud, Database, FileText, Globe2, Plug, Rss, Server, Upload, FilePlus, RotateCw } from "lucide-react";

interface DataSource {
  id: string; name: string; connector_key: string;
  status: string; workspace: string; has_secret?: boolean;
  metadata_profile_binding?: { profile_id: string; version: number; digest: string } | null;
}
interface MetadataProfileSummary { id: string; name: string; published_version: number | null }
interface IngestionRun {
  id: string; source: string; status: string;
  started_at?: string | null; finished_at?: string | null;
  items_total?: number; items_ok?: number; items_failed?: number;
  error?: string; cursor?: Record<string, unknown>;
}
interface SchemaProperty {
  type?: string;
  description?: string;
  // JSON-Schema doesn't have a native "password" format, so connectors
  // tag secret fields with x-format=password. The wizard renders these
  // as <input type="password"> regardless of type.
  "x-format"?: string;
  format?: string;
}
interface JsonSchemaObject {
  type?: string;
  required?: string[];
  properties?: Record<string, SchemaProperty>;
}
interface Connector {
  key: string;
  label?: string;
  description?: string;
  capabilities?: string[];
  config_schema?: JsonSchemaObject;
  secret_schema?: JsonSchemaObject;
}
interface IngestResponse {
  mode: "sync" | "async";
  ingestion_run: IngestionRun;
  document?: { id: string; title: string; created: boolean };
}
interface DiscoveryResponse {
  run_id: string;
  status: string;
  proposal_count: number;
  created_count: number;
  conflict_count: number;
  disclosure_version?: string;
}

/** Supplied brand marks sit on one light tile so black and colored marks read in both themes. */
const CONNECTOR_BRANDS: Record<string, string> = {
  s3: "s3.png",
  "cloud.gcs": "gcs.jpeg",
  "cloud.azure_blob": "azure.png",
  "saas.gdrive": "drive.png",
  "saas.confluence": "confluence.png",
  "saas.jira": "jira.png",
  "saas.salesforce": "salesforce.png",
  "saas.slack": "slack.png",
  "saas.sharepoint": "sharepoint.png",
  "saas.notion": "notion.png",
  "db.mongodb": "mongodb.png",
  "db.mysql": "mysql.png",
  postgres: "postgres.png",
  "warehouse.snowflake": "snowflake.png",
  "stream.kafka": "kafka.png",
  "web.rss": "rss.png",
  "api.graphql": "graphql.png",
};

function ConnectorGlyph({ connectorKey, prominent = false }: { connectorKey: string; prominent?: boolean }) {
  const key = connectorKey.toLowerCase();
  const brand = CONNECTOR_BRANDS[key];
  const props = { size: prominent ? 19 : 15, className: "shrink-0 text-[#216080]", "aria-hidden": true };
  const icon = key === "web.rss" ? <Rss {...props} />
    : key.startsWith("web.") || key === "api.rest" || key === "api.graphql" ? <Globe2 {...props} />
      : key.startsWith("cloud.") || key.includes("s3") || key.includes("drive") || key.includes("storage") ? <Cloud {...props} />
        : key.includes("postgres") || key.includes("mongo") || key.includes("database") ? <Database {...props} />
          : key.includes("mcp") || key.includes("webhook") ? <Server {...props} />
            : key === "upload" ? <FileText {...props} /> : <Plug {...props} />;
  return <span className={`inline-flex shrink-0 items-center justify-center border border-border bg-[#f8fbfe] ${prominent ? "h-10 w-10 p-1.5" : "h-7 w-7 p-1"}`} aria-hidden="true">
    {brand ? <img src={`/connector-brands/${brand}`} alt="" loading="lazy" className="max-h-full max-w-full object-contain" /> : icon}
  </span>;
}

/**
 * Catalog — single pane for everything that puts data into a workspace.
 *
 *   - "Quick ingest"  : paste plain text. ≤50 KB processed inline,
 *                       larger goes async via the ingestion queue.
 *   - "Upload file"   : multipart drop zone for PDF / DOCX / images /
 *                       text. Files are stored on S3-compatible object
 *                       storage and parsed by `apps.catalog.tasks
 *                       .process_document` (pypdf → OCR fallback for
 *                       scanned PDFs, pytesseract for images, python-
 *                       docx for Office docs). Status is polled from
 *                       /catalog/runs/<id>/ until terminal.
 *   - "Connectors"    : registry of pluggable backends (S3, MCP, REST,
 *                       Postgres …) so users know what they can wire.
 *
 * Both lower tables (Data sources / Recent ingestion runs) use the new
 * <PaginatedTable> so they scale past a handful of rows with search,
 * status filter, page-size and Prev/Next.
 */
export function Catalog() {
  const ws = useApp((s) => s.workspaceId);
  const { canEdit, canAdmin, isLoading: roleLoading, error: roleError } = useWorkspacePermissions();
  const qc = useQueryClient();
  const [tab, setTab] = useState<"text" | "discovery" | "review" | "file" | "connectors">(
    () => new URLSearchParams(window.location.search).get("tab") === "review" ? "review" : "text",
  );

  // Quick-ingest text form ------------------------------------------------
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [discoveryText, setDiscoveryText] = useState("");
  const [discoveryMode, setDiscoveryMode] = useState<"deterministic" | "ai">("deterministic");
  const [discoveryPackId, setDiscoveryPackId] = useState("");
  const [discoveryFormat, setDiscoveryFormat] = useState<"csv" | "ddl" | "json_schema">("csv");
  const [discoveryIndustry, setDiscoveryIndustry] = useState("insurance");
  const [discoveryDomain, setDiscoveryDomain] = useState("motor");
  const [discoveryLob, setDiscoveryLob] = useState("motor");
  const [discoveryProduct, setDiscoveryProduct] = useState("personal_motor");
  const [discoveryReceipt, setDiscoveryReceipt] = useState<DiscoveryResponse | null>(null);

  // Track the most recent async run so the user sees live status
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  // Wizard state — null = closed
  const [wizardKey, setWizardKey] = useState<string | null>(null);

  const connectors = useQuery<{ connectors: Connector[] }>({
    queryKey: ["connectors"],
    queryFn: () => rawFetch(`/catalog/sources/connectors/`),
  });
  const installedPacks = useQuery<{ results: { pack: string }[] }>({
    queryKey: ["installed-packs", ws],
    queryFn: () => rawFetch(`/packs/installs/?workspace_id=${ws}&page_size=100`),
    enabled: !!ws && tab === "discovery",
  });
  const publishedPacks = useQuery<{ results: { id: string; name: string; semver: string }[] }>({
    queryKey: ["published-packs"],
    queryFn: () => rawFetch("/packs/packs/?page_size=100"),
    enabled: tab === "discovery",
  });

  const ingestText = useMutation<IngestResponse>({
    mutationFn: () => rawFetch<IngestResponse>(`/catalog/sources/quick_ingest/`, {
      method: "POST",
      body: JSON.stringify({ workspace: ws, title, text }),
    }),
    onSuccess: (resp) => {
      setTitle(""); setText("");
      if (resp.mode === "async") setActiveRunId(resp.ingestion_run.id);
      qc.invalidateQueries({ queryKey: ["table", "/catalog/sources/"] });
      qc.invalidateQueries({ queryKey: ["table", "/catalog/runs/"] });
    },
  });

  const discoverPaste = useMutation<DiscoveryResponse>({
    mutationFn: () => rawFetch<DiscoveryResponse>(
      discoveryMode === "ai" ? "/catalog/discovery/ai/" : "/catalog/discovery/paste/", {
      method: "POST",
      body: JSON.stringify({
        workspace: ws, text: discoveryText, format: discoveryFormat,
        industry: discoveryIndustry, domain: discoveryDomain,
        lob: discoveryLob, product: discoveryProduct,
        ...(discoveryMode === "ai" && discoveryPackId ? { pack_id: discoveryPackId } : {}),
      }),
    }),
    onSuccess: (receipt) => {
      setDiscoveryReceipt(receipt);
      setDiscoveryText("");
      qc.invalidateQueries({ queryKey: ["table", "/catalog/runs/"] });
    },
  });

  const ingestFile = useMutation<IngestResponse, Error, File>({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append("workspace", String(ws));
      fd.append("file", file);
      fd.append("title", file.name);
      // rawFetch sets Content-Type: application/json by default — talk to
      // fetch directly so the browser sets the multipart boundary.
      const token = localStorage.getItem("ckos.token");
      const slug = localStorage.getItem("ckos.tenantSchema");
      const headers: Record<string, string> = {};
      if (token) headers["Authorization"] = `Token ${token}`;
      if (slug) headers["X-Tenant-Schema"] = slug;
      const base = import.meta.env.VITE_API_BASE_URL || "/api/v1";
      const r = await fetch(`${base}/catalog/sources/upload/`,
                            { method: "POST", body: fd, headers });
      if (!r.ok) throw await parseApiError(r);
      return (await r.json()) as IngestResponse;
    },
    onSuccess: (resp) => {
      setActiveRunId(resp.ingestion_run.id);
      qc.invalidateQueries({ queryKey: ["table", "/catalog/sources/"] });
      qc.invalidateQueries({ queryKey: ["table", "/catalog/runs/"] });
    },
  });

  // Live status polling for the async run we just triggered
  const activeRun = useQuery<IngestionRun>({
    queryKey: ["ingestion-run", activeRunId],
    queryFn: () => rawFetch<IngestionRun>(`/catalog/runs/${activeRunId}/`),
    enabled: !!activeRunId,
    refetchInterval: (q) => {
      const s = q.state.data?.status;
      return s === "succeeded" || s === "failed" || s === "cancelled" ? false : 2000;
    },
  });

  // These tables are independent queries. Refresh at terminal completion,
  // rather than only when the ingestion request is first queued.
  useEffect(() => {
    const status = activeRun.data?.status;
    if (status === "succeeded" || status === "failed" || status === "cancelled") {
      qc.invalidateQueries({ queryKey: ["table", "/catalog/sources/"] });
      qc.invalidateQueries({ queryKey: ["table", "/catalog/runs/"] });
    }
  }, [activeRun.data?.status, qc]);

  // Native drag/drop on the file tab
  const dropRef = useRef<HTMLLabelElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  useEffect(() => {
    const el = dropRef.current; if (!el) return;
    const over = (e: DragEvent) => { e.preventDefault(); if (canEdit) setDragOver(true); };
    const leave = () => setDragOver(false);
    const drop = (e: DragEvent) => {
      e.preventDefault(); setDragOver(false);
      const f = e.dataTransfer?.files?.[0];
      if (f && canEdit) ingestFile.mutate(f);
    };
    el.addEventListener("dragover", over);
    el.addEventListener("dragleave", leave);
    el.addEventListener("drop", drop);
    return () => {
      el.removeEventListener("dragover", over);
      el.removeEventListener("dragleave", leave);
      el.removeEventListener("drop", drop);
    };
  }, [ingestFile, canEdit]);

  if (!ws) {
    return (
      <>
        <PageHeader title="Catalog" subtitle="Data sources and ingestion runs." />
        <EmptyState title="No workspace selected"
                    hint="Pick a workspace from the top-bar selector." />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title="Catalog"
        subtitle="Ingest searchable content or stage ontology proposals from a structured paste."
        help={
          <>
            <p>Searchable paste, upload and connectors create governed Documents and Chunks. Discovery-only paste stages reviewable ontology proposals without Documents or Chunks.</p>
            <p>Uploads are parsed and chunked, with best-effort embeddings. Building a semantic layer is a separate, governed step: run extraction, review candidate terms and relationships, then publish them. Upload alone does not do that automatically.</p>
          </>
        }
        learnMore={{ to: "/howto", hash: "catalog", label: "Sources & ingestion" }}
      />

      {!roleLoading && !canEdit && (
        <div className="card mb-4 text-xs text-muted" role="status">
          {roleError ? "Unable to verify your workspace role. Editing is unavailable." :
            "This workspace is read-only for your role. An editor or admin can ingest and manage sources."}
        </div>
      )}

      {/* --------- Action bar ---------- */}
      <div className="card mb-4">
        <div className="mb-4 space-y-3">
          <div role="tablist" aria-label="Catalog workflow" className="grid gap-3 xl:grid-cols-[3fr_2fr]">
            <div className="min-w-0 border border-border bg-bg p-2">
              <p className="px-2 pb-2 font-mono text-[10px] uppercase tracking-wider text-muted" role="presentation">01 / Bring content in</p>
              <div className="grid gap-1.5 sm:grid-cols-3">
                <TabBtn active={tab === "text"} onClick={() => setTab("text")}
                  icon={<FileText size={14} />} label="Paste searchable text" />
                <TabBtn active={tab === "file"} onClick={() => setTab("file")}
                  icon={<FilePlus size={14} />} label="Upload a file" />
                <TabBtn active={tab === "connectors"} onClick={() => setTab("connectors")}
                  icon={<Plug size={14} />} label="Connect a source" />
              </div>
            </div>
            <div className="min-w-0 border border-border bg-bg p-2">
              <p className="px-2 pb-2 font-mono text-[10px] uppercase tracking-wider text-muted" role="presentation">02 / Build meaning</p>
              <div className="grid gap-1.5 sm:grid-cols-2">
                <TabBtn active={tab === "discovery"} onClick={() => setTab("discovery")}
                  icon={<FileText size={14} />} label="Discover terms" />
                <TabBtn active={tab === "review"} onClick={() => setTab("review")}
                  icon={<FileText size={14} />} label="Review proposals" />
              </div>
            </div>
          </div>
          <p className="border-l-2 border-accent/70 pl-3 text-[11px] leading-relaxed text-muted">
            <span className="mr-2 font-mono uppercase tracking-wider text-accent">Data handling</span>
            Searchable ingest stores Documents and Chunks. Discovery paste stages proposals without them.
          </p>
        </div>

        {tab === "text" && (
          <div>
            <div className="grid grid-cols-1 gap-2 md:grid-cols-[1fr_3fr]">
              <input className="input" placeholder="Document title (e.g. Policy ACME-CP-2025-0001)"
                     value={title} onChange={(e) => setTitle(e.target.value)} disabled={!canEdit} />
              <textarea className="input min-h-[140px] font-mono text-xs"
                        placeholder="Paste any policy wording, FNOL, contract, KB article…"
                        value={text} onChange={(e) => setText(e.target.value)} disabled={!canEdit} />
            </div>
            {ingestText.isError && <div className="mt-2"><ErrorBox error={ingestText.error} /></div>}
            <div className="mt-3 flex items-center justify-between">
              <div className="text-[11px] text-muted">
                ≤50 KB is chunked inline; larger payloads go async on the
                <code className="mx-1">ingestion</code>queue. Use a Pipeline / Pack
                for OCR + embedding.
              </div>
              <button className="btn-primary"
                      disabled={!canEdit || !title || !text || ingestText.isPending}
                      onClick={() => ingestText.mutate()}>
                <Upload size={14} className="-mt-0.5 mr-1 inline" />
                {ingestText.isPending ? "Ingesting…" : "Ingest text"}
              </button>
            </div>
          </div>
        )}

        {tab === "discovery" && (
          <div className="space-y-3">
            <p className="text-xs text-muted">
              Stage reviewable terms without publishing an ontology. Structured import is deterministic. Free-text suggestions use only the workspace's selected Catalog AI key.
            </p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Discovery method">
              <button className={discoveryMode === "deterministic" ? "btn-primary" : "btn"}
                      onClick={() => setDiscoveryMode("deterministic")}>Structured, no AI</button>
              <button className={discoveryMode === "ai" ? "btn-primary" : "btn"}
                      onClick={() => setDiscoveryMode("ai")}>Free text with my LLM key</button>
            </div>
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
              {discoveryMode === "deterministic" && <label className="text-xs text-muted">Format
                <select className="input mt-1 w-full" value={discoveryFormat}
                        onChange={(e) => setDiscoveryFormat(e.target.value as typeof discoveryFormat)} disabled={!canEdit}>
                  <option value="csv">CSV dictionary</option>
                  <option value="ddl">SQL DDL</option>
                  <option value="json_schema">JSON Schema</option>
                </select>
              </label>}
              {([
                ["Industry", discoveryIndustry, setDiscoveryIndustry],
                ["Domain", discoveryDomain, setDiscoveryDomain],
                ["Line of business", discoveryLob, setDiscoveryLob],
                ["Product", discoveryProduct, setDiscoveryProduct],
              ] as const).map(([label, value, setter]) => (
                <label key={label} className="text-xs text-muted">{label}
                  <input className="input mt-1 w-full" value={value}
                         onChange={(e) => setter(e.target.value)} disabled={!canEdit}
                         maxLength={40} pattern="[A-Za-z0-9][A-Za-z0-9_-]*" />
                </label>
              ))}
            </div>
            {discoveryMode === "ai" && <label className="block text-xs text-muted">Installed domain pack (optional)
              <select className="input mt-1 w-full" value={discoveryPackId}
                      onChange={(e) => setDiscoveryPackId(e.target.value)}>
                <option value="">No pack selected</option>
                {publishedPacks.data?.results.filter((pack) => installedPacks.data?.results.some((row) => row.pack === pack.id))
                  .map((pack) => <option key={pack.id} value={pack.id}>{pack.name} {pack.semver}</option>)}
              </select>
            </label>}
            <textarea className="input min-h-[170px] w-full font-mono text-xs"
                      aria-label={discoveryMode === "ai" ? "Free-text discovery source" : "Structured discovery text"}
                      placeholder={discoveryMode === "ai" ? "Paste source text for cited term suggestions…" : "Paste CSV dictionary rows, CREATE TABLE declarations, or JSON Schema definitions…"}
                      value={discoveryText} onChange={(e) => setDiscoveryText(e.target.value)} disabled={!canEdit} />
            <div className="rounded border border-border bg-bg px-3 py-2 text-[11px] text-muted">
              The pasted source is processed in memory. Redacted definitions, evidence quotes and run metadata remain in the managed tenant database and its backups. Backup expiry has not been verified; do not paste sensitive data in this pilot. No original paste, Document, Chunk or embedding is retained by this discovery path. {discoveryMode === "ai" ? "Redacted text goes to your selected provider; unsafe names or addresses are refused. Disable Catalog AI in Settings to stop model calls." : "The structured path makes no model call."}
            </div>
            {discoverPaste.isError && <ErrorBox error={discoverPaste.error} />}
            {discoveryReceipt && (
              <div role="status" aria-live="polite" className="rounded border border-accent/40 bg-accent/10 px-3 py-2 text-xs text-fg">
                Run {shortId(discoveryReceipt.run_id)}: {discoveryReceipt.status}. {discoveryReceipt.proposal_count} proposals staged ({discoveryReceipt.created_count} new, {discoveryReceipt.conflict_count} needing conflict review). No ontology was published.
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              <span className="text-[11px] text-muted">
                {new TextEncoder().encode(discoveryText).length.toLocaleString()} / 50,000 bytes · editor role required · hourly workspace cap
              </span>
              <button className="btn-primary" disabled={!canEdit || discoverPaste.isPending ||
                      !discoveryText.trim() || new TextEncoder().encode(discoveryText).length > 50_000}
                      onClick={() => { setDiscoveryReceipt(null); discoverPaste.mutate(); }}>
                {discoverPaste.isPending ? "Checking…" : "Stage proposals"}
              </button>
            </div>
            {canEdit && ws && <DictionaryImporter workspaceId={ws} />}
            {canAdmin && ws && <CloudDictionaryBrowser workspaceId={ws} />}
          </div>
        )}

        {tab === "review" && <DiscoveryReviewPanel workspaceId={ws ?? ""}
          canEdit={canEdit} canAdmin={canAdmin} />}

        {tab === "file" && (
          <div>
            <label
              ref={dropRef}
              className={`flex h-36 w-full ${canEdit ? "cursor-pointer" : "cursor-not-allowed opacity-60"} flex-col items-center
                          justify-center rounded border-2 border-dashed
                          ${dragOver ? "border-accent bg-accent/10" : "border-border"}
                          text-sm text-muted transition`}>
              <input type="file" className="hidden" disabled={!canEdit}
                     accept=".pdf,.txt,.md,.docx,.png,.jpg,.jpeg,.gif,.webp,.bmp,.tif,.tiff"
                     onChange={(e) => {
                       const f = e.target.files?.[0]; if (f && canEdit) ingestFile.mutate(f);
                       e.target.value = "";
                     }} />
              <FilePlus size={28} className="mb-1 text-accent" />
              <span><strong>Click to choose a file</strong> or drop one here</span>
              <span className="mt-1 text-[11px]">
                PDF · DOCX · TXT · MD · PNG · JPG · TIFF — up to 50 MB
              </span>
            </label>
            {ingestFile.isError && <div className="mt-2"><ErrorBox error={ingestFile.error} /></div>}
            <div className="mt-2 text-[11px] text-muted">
              File upload requires your workspace S3 bucket configured in Settings.
              Original bytes go to that bucket, then a Celery
              worker parses them: <code>pypdf</code> for digital PDFs (with
              <code className="mx-1">pytesseract</code>+<code>pdf2image</code> OCR
              fallback for scans), <code>pytesseract</code> for images,
              <code className="mx-1">python-docx</code> for Office docs.
              Parsed text, chunks, and optional embeddings are retained in
              the workspace database; choosing a bucket does not make ingestion transient.
            </div>
          </div>
        )}

        {tab === "connectors" && (
          <div>
            {connectors.isLoading && (
              <div role="status" className="rounded border border-border bg-bg p-3 text-xs text-muted">
                Loading available connectors…
              </div>
            )}
            {connectors.isError && <ErrorBox error={connectors.error} />}
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {(connectors.data?.connectors ?? []).map((c) => (
                <div key={c.key}
                     className="flex flex-col gap-1 rounded border border-border bg-bg p-3 text-xs">
                  <div className="flex items-center gap-2">
                    <ConnectorGlyph connectorKey={c.key} prominent />
                    <span className="truncate font-medium text-fg">{c.label || c.key}</span>
                    <code className="ml-auto font-mono text-[10px] text-muted">{c.key}</code>
                  </div>
                  {c.description && (
                    <p className="text-[11px] text-muted">{c.description}</p>
                  )}
                  {c.capabilities && c.capabilities.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {c.capabilities.map((cap) => (
                        <span key={cap}
                              className="rounded bg-accent/10 px-1.5 py-0.5 text-[10px] text-accent">
                          {cap}
                        </span>
                      ))}
                    </div>
                  )}
                  <button className="btn-primary mt-1 self-end text-[11px]"
                          disabled={!canEdit}
                          onClick={() => setWizardKey(c.key)}>
                    Connect
                  </button>
                </div>
              ))}
              {connectors.data && connectors.data.connectors.length === 0 && (
                <span className="text-muted">No connectors registered.</span>
              )}
            </div>
            <div className="mt-3 text-[11px] text-muted">
              Click <strong>Connect</strong> to enter credentials, test the
              connection, and save a DataSource. Encrypted at rest with the
              tenant key.
            </div>
          </div>
        )}
      </div>

      {/* --------- Async run live status ---------- */}
      {activeRunId && activeRun.isError && (
        <div className="mb-4" role="status" aria-live="polite">
          <ErrorBox error={activeRun.error} />
          <div className="mt-2 flex gap-2">
            <button className="btn-ghost text-xs" onClick={() => activeRun.refetch()}>
              Retry status check
            </button>
            <button className="btn-ghost text-xs" onClick={() => setActiveRunId(null)}>
              Dismiss
            </button>
          </div>
        </div>
      )}
      {activeRunId && activeRun.data && (
        <div className="card mb-4 border-accent/40" role="status" aria-live="polite">
          <div className="flex items-center gap-3 text-sm">
            <StatusPill status={activeRun.data.status} />
            <span className="font-mono text-xs">{shortId(activeRun.data.id)}</span>
            <span className="text-xs text-muted">
              Started {fmtDate(activeRun.data.started_at)} · Finished {fmtDate(activeRun.data.finished_at)}
            </span>
            <button className="btn-ghost ml-auto text-xs"
                    onClick={() => setActiveRunId(null)}>Dismiss</button>
          </div>
          {activeRun.data.error && (
            <div className="mt-2 rounded border border-bad/40 bg-bad/10 p-2 text-xs text-bad">
              {activeRun.data.error}
            </div>
          )}
          {activeRun.data.status === "pending" && (
            <div className="mt-2 text-[11px] text-muted">
              Queued for a worker. This panel refreshes automatically.
            </div>
          )}
          {activeRun.data.status === "succeeded" && activeRun.data.cursor && (
            <div className="mt-2 text-[11px] text-muted">
              {String(activeRun.data.cursor.chunks ?? "?")} chunks ·
              parser <code>{String(activeRun.data.cursor.parser ?? "?")}</code>
              {activeRun.data.cursor.ocr === true ? " · OCR used" : ""}
            </div>
          )}
        </div>
      )}

      {/* --------- Data sources ---------- */}
      <h2 className="mb-2 text-sm font-medium text-muted">Data sources</h2>
      <PaginatedTable<DataSource>
        endpoint="/catalog/sources/"
        baseParams={{ workspace: ws }}
        statusOptions={[
          { label: "Active", value: "active" },
          { label: "Paused", value: "paused" },
          { label: "Error",  value: "error"  },
        ]}
        searchPlaceholder="Search by name or connector…"
        cols={[
          { header: "Name",      cell: (d) => d.name },
          { header: "Connector", cell: (d) => (
              <span className="flex items-center gap-1.5">
                <ConnectorGlyph connectorKey={d.connector_key} />
                <code className="font-mono text-xs">{d.connector_key}</code>
              </span>
            ) },
          { header: "Status",    cell: (d) => <StatusPill status={d.status} /> },
          { header: "Secret",    cell: (d) => d.has_secret
              ? <span className="text-[11px] text-accent">set</span>
              : <span className="text-[11px] text-muted">—</span> },
          { header: "Context", cell: (d) =>
              <SourceMetadataProfileControl source={d} workspace={ws!} canEdit={canEdit} /> },
          { header: "ID",        cell: (d) => <code className="font-mono text-xs">{shortId(d.id)}</code> },
          { header: "",          cell: (d) => canEdit ? (
              <SyncButton
                sourceId={d.id}
                paused={d.status === "paused"}
                onRun={(rid) => setActiveRunId(rid)}
              />
            ) : null },
        ]}
        empty="No data sources yet — paste text or upload a file above."
      />

      {/* --------- Ingestion runs ---------- */}
      <h2 className="mb-2 mt-6 text-sm font-medium text-muted">Ingestion runs</h2>
      <PaginatedTable<IngestionRun>
        endpoint="/catalog/runs/"
        baseParams={{ workspace: ws }}
        statusOptions={[
          { label: "Pending",   value: "pending"   },
          { label: "Running",   value: "running"   },
          { label: "Succeeded", value: "succeeded" },
          { label: "Failed",    value: "failed"    },
          { label: "Cancelled", value: "cancelled" },
        ]}
        searchPlaceholder="Search by error / source name…"
        refetchInterval={5000}
        cols={[
          { header: "Run",      cell: (r) => <code className="font-mono text-xs">{shortId(r.id)}</code> },
          { header: "Source",   cell: (r) => <code className="font-mono text-xs">{shortId(r.source)}</code> },
          { header: "Status",   cell: (r) => <StatusPill status={r.status} /> },
          { header: "Items",    cell: (r) => `${r.items_ok ?? 0}/${r.items_total ?? 0}` },
          { header: "Started",  cell: (r) => fmtDate(r.started_at) },
          { header: "Finished", cell: (r) => fmtDate(r.finished_at) },
          { header: "", width: "1%", cell: (r) =>
              !canEdit ? null : r.status === "failed" ? <RecoverRunButton runId={r.id} />
                : (r.status === "pending" || r.status === "running")
                  ? <CancelRunButton runId={r.id} /> : null,
          },
        ]}
        empty="No runs yet."
      />

      <div className="mt-6 text-[11px] text-muted">
        Looking for ready-made ontology + agents + sample docs?{" "}
        <Link to="/marketplace" className="text-accent underline">
          Browse the Marketplace
        </Link>{" "}
        and install a domain Pack (Insurance, Health, Legal, Tax, …).
      </div>

      {wizardKey && canEdit && (
        <ConnectorWizardModal
          connector={(connectors.data?.connectors ?? []).find(c => c.key === wizardKey)!}
          workspace={ws}
          onClose={() => setWizardKey(null)}
          onSaved={(runId) => {
            setWizardKey(null);
            if (runId) setActiveRunId(runId);
            qc.invalidateQueries({ queryKey: ["table", "/catalog/sources/"] });
            qc.invalidateQueries({ queryKey: ["table", "/catalog/runs/"] });
          }}
        />
      )}
    </>
  );
}

/** Tiny tab-button helper; keeps the JSX above readable. */
function TabBtn({ active, onClick, icon, label }: {
  active: boolean; onClick: () => void;
  icon: React.ReactNode; label: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={`flex min-h-12 min-w-0 items-center justify-center gap-2 border px-2 py-2 text-center text-xs leading-tight transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent sm:text-sm ${
        active ? "border-accent/60 bg-accent/10 font-medium text-accent" : "border-transparent text-muted hover:border-border hover:bg-border/40 hover:text-fg"}`}
      onClick={onClick}>
      <span className="shrink-0" aria-hidden="true">{icon}</span><span className="min-w-0">{label}</span>
    </button>
  );
}

/** Per-row "Run sync" button on the Data sources table. */
function SyncButton({ sourceId, paused, onRun }: {
  sourceId: string; paused: boolean; onRun: (runId: string) => void;
}) {
  const qc = useQueryClient();
  const m = useMutation<IngestionRun>({
    mutationFn: () => rawFetch<IngestionRun>(
      `/catalog/sources/${sourceId}/sync/`, { method: "POST" }),
    onSuccess: (r) => {
      onRun(r.id);
      qc.invalidateQueries({ queryKey: ["table", "/catalog/sources/"] });
      qc.invalidateQueries({ queryKey: ["table", "/catalog/runs/"] });
    },
  });
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        className="btn-ghost text-[11px]"
        title={paused
          ? "This source is paused"
          : "Trigger an ingestion run via this connector"}
        disabled={paused || m.isPending}
        onClick={() => { m.reset(); m.mutate(); }}>
        {paused ? "Paused" : m.isPending ? "Syncing…" : "Run sync"}
      </button>
      {m.isError && (
        <span role="alert" className="max-w-48 text-right text-[11px] text-bad">
          {m.error instanceof Error ? m.error.message : "Sync could not be started."}
        </span>
      )}
    </div>
  );
}

/** Per-row recover button for stuck PENDING / FAILED ingestion runs. */
function RecoverRunButton({ runId }: { runId: string }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => rawFetch(`/catalog/runs/${runId}/recover/`, {
      method: "POST", body: JSON.stringify({}),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["table", "/catalog/runs/"] }),
  });
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        className="btn-ghost text-[11px]"
        title="Re-dispatch this ingestion run"
        disabled={m.isPending}
        onClick={() => { m.reset(); m.mutate(); }}>
        <RotateCw size={11} className="-mt-0.5 mr-1 inline" />
        {m.isPending ? "…" : "Recover"}
      </button>
      {m.isError && (
        <span role="alert" className="max-w-48 text-right text-[11px] text-bad">
          {m.error instanceof Error ? m.error.message : "Recovery could not be started."}
        </span>
      )}
    </div>
  );
}

/** Per-row cancellation control for queued and active ingestion runs. */
function CancelRunButton({ runId }: { runId: string }) {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: () => rawFetch(`/catalog/runs/${runId}/cancel/`, {
      method: "POST", body: JSON.stringify({}),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["table", "/catalog/runs/"] }),
  });
  return (
    <div className="flex flex-col items-end gap-1">
      <button
        className="btn-ghost text-[11px] text-bad"
        title="Cancel this ingestion run"
        disabled={m.isPending}
        onClick={() => { m.reset(); m.mutate(); }}>
        {m.isPending ? "Cancelling…" : "Cancel"}
      </button>
      {m.isError && (
        <span role="alert" className="max-w-48 text-right text-[11px] text-bad">
          {m.error instanceof Error ? m.error.message : "Cancellation could not be completed."}
        </span>
      )}
    </div>
  );
}

/**
 * ConnectorWizardModal — a generic JSON-Schema driven form so that
 * adding a new connector on the BE automatically gets a working UI.
 *
 * Renders one input per ``properties`` entry from ``config_schema``
 * and ``secret_schema``. Secret fields are detected via the
 * ``x-format=password`` hint and rendered as <input type="password">.
 * Object-typed fields fall back to a JSON textarea (e.g. the kafka
 * client_kwargs or service-account JSON blobs).
 */
function ConnectorWizardModal({ connector, workspace, onClose, onSaved }: {
  connector: Connector;
  workspace: string;
  onClose: () => void;
  onSaved: (runId: string | null) => void;
}) {
  const [name, setName] = useState(`${connector.label || connector.key} source`);
  const [config, setConfig] = useState<Record<string, unknown>>({});
  const [secret, setSecret] = useState<Record<string, unknown>>({});
  const [metadataProfileId, setMetadataProfileId] = useState("");
  const [testResult, setTestResult] = useState<{ ok: boolean; error?: string } | null>(null);
  const metadataProfiles = useQuery<MetadataProfileSummary[]>({
    queryKey: ["metadata-profiles", workspace],
    queryFn: () => rawFetch(`/pipelines/metadata-profiles/?workspace=${encodeURIComponent(workspace)}`),
  });

  const test = useMutation<{ ok: boolean; error?: string }, Error>({
    mutationFn: async () => {
      // Hit the endpoint directly because rawFetch throws on 4xx and we
      // want the JSON body for a clean error message in the UI.
      const token = localStorage.getItem("ckos.token");
      const slug = localStorage.getItem("ckos.tenantSchema");
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (token) headers["Authorization"] = `Token ${token}`;
      if (slug) headers["X-Tenant-Schema"] = slug;
      const base = import.meta.env.VITE_API_BASE_URL || "/api/v1";
      const r = await fetch(`${base}/catalog/sources/test_connection/?workspace=${encodeURIComponent(workspace)}`, {
        method: "POST", headers,
        body: JSON.stringify({
          connector_key: connector.key, config, secret,
        }),
      });
      if (!r.ok) throw await parseApiError(r);
      try {
        return await r.json();
      } catch {
        return { ok: false, error: `${r.status} ${r.statusText}` };
      }
    },
    onSuccess: setTestResult,
    onError: (error) => setTestResult({ ok: false, error: error.message }),
  });

  type CreatedDataSource = DataSource & { __runId?: string };
  const save = useMutation<CreatedDataSource, Error, { runAfter: boolean }>({
    mutationFn: async ({ runAfter }) => {
      const ds = await rawFetch<CreatedDataSource>(`/catalog/sources/`, {
        method: "POST",
        body: JSON.stringify({
          workspace, name, connector_key: connector.key,
          config,
          metadata_profile_id: metadataProfileId || null,
          ...(Object.keys(secret).length ? { secret } : {}),
        }),
      });
      if (runAfter) {
        const run = await rawFetch<IngestionRun>(
          `/catalog/sources/${ds.id}/sync/`, { method: "POST" });
        ds.__runId = run.id;
      }
      return ds;
    },
    onSuccess: (ds) => onSaved(ds.__runId ?? null),
  });

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 p-4"
         onClick={onClose}>
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-start justify-between">
          <div>
            <h3 className="text-base font-medium">Connect: {connector.label || connector.key}</h3>
            <p className="text-[11px] text-muted">{connector.description}</p>
          </div>
          <button className="btn-ghost text-xs" onClick={onClose}>Close</button>
        </div>

        <div className="mb-3">
          <label className="block text-[11px] text-muted">Source name</label>
          <input className="input w-full" value={name}
                 onChange={(e) => setName(e.target.value)} />
        </div>
        <label className="mb-3 block text-[11px] text-muted">Metadata profile (optional, pinned published version)
          <select className="input mt-1 w-full" value={metadataProfileId}
                  onChange={(event) => setMetadataProfileId(event.target.value)}>
            <option value="">No metadata profile</option>
            {(metadataProfiles.data ?? []).filter((profile) => profile.published_version).map((profile) =>
              <option key={profile.id} value={profile.id}>{profile.name} · v{profile.published_version}</option>)}
          </select>
          <span className="mt-1 block">Context tags flow into new documents and chunks. Shared, deduplicated content keeps per-source context on its ingestion link.</span>
        </label>

        <SchemaForm
          title="Configuration"
          schema={connector.config_schema}
          values={config}
          onChange={setConfig}
        />
        <SchemaForm
          title="Credentials"
          schema={connector.secret_schema}
          values={secret}
          onChange={setSecret}
          hint="Stored encrypted under your tenant key. Never echoed back."
        />

        {testResult && (
          <div className={`mt-2 rounded p-2 text-xs ${
            testResult.ok ? "bg-good/10 text-good" : "bg-bad/10 text-bad"}`}>
            {testResult.ok ? "Connection OK." : `Failed: ${testResult.error}`}
          </div>
        )}
        {save.isError && <div className="mt-2"><ErrorBox error={save.error} /></div>}

        <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
          <button className="btn-ghost"
                  disabled={test.isPending}
                  onClick={() => test.mutate()}>
            {test.isPending ? "Testing…" : "Test connection"}
          </button>
          <button className="btn-ghost"
                  disabled={save.isPending || !name}
                  onClick={() => save.mutate({ runAfter: false })}>
            {save.isPending ? "Saving…" : "Save"}
          </button>
          <button className="btn-primary"
                  disabled={save.isPending || !name}
                  onClick={() => save.mutate({ runAfter: true })}>
            Save & sync
          </button>
        </div>
      </div>
    </div>
  );
}

function SourceMetadataProfileControl({ source, workspace, canEdit }: {
  source: DataSource; workspace: string; canEdit: boolean;
}) {
  const qc = useQueryClient();
  const profiles = useQuery<MetadataProfileSummary[]>({
    queryKey: ["metadata-profiles", workspace],
    queryFn: () => rawFetch(`/pipelines/metadata-profiles/?workspace=${encodeURIComponent(workspace)}`),
    enabled: canEdit,
  });
  const update = useMutation({
    mutationFn: (profileId: string) => rawFetch(`/catalog/sources/${source.id}/`, {
      method: "PATCH", body: JSON.stringify({ metadata_profile_id: profileId || null }),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["table", "/catalog/sources/"] }),
  });
  if (!canEdit) return <span className="text-xs text-muted">{source.metadata_profile_binding
    ? `Metadata v${source.metadata_profile_binding.version}` : "None"}</span>;
  return <div className="min-w-40">
    <select aria-label={`Metadata profile for ${source.name}`} className="input w-full text-xs"
      value={source.metadata_profile_binding?.profile_id ?? ""}
      disabled={update.isPending || profiles.isLoading || profiles.isError}
      onChange={(event) => update.mutate(event.target.value)}>
      <option value="">No profile</option>
      {(profiles.data ?? []).filter((profile) => profile.published_version).map((profile) =>
        <option key={profile.id} value={profile.id}>{profile.name} · v{profile.published_version}</option>)}
    </select>
    {update.isError && <span className="text-[10px] text-bad" role="alert">Could not update profile.</span>}
  </div>;
}

/** Render a flat JSON-Schema object as a form. */
function SchemaForm({ title, schema, values, onChange, hint }: {
  title: string;
  schema: JsonSchemaObject | undefined;
  values: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
  hint?: string;
}) {
  const props = schema?.properties ?? {};
  const required = new Set(schema?.required ?? []);
  const keys = Object.keys(props);
  if (keys.length === 0) {
    return (
      <div className="mb-2">
        <h4 className="text-xs font-medium text-muted">{title}</h4>
        <p className="text-[11px] text-muted">No fields required.</p>
      </div>
    );
  }
  return (
    <div className="mb-3">
      <h4 className="mb-1 text-xs font-medium text-muted">{title}</h4>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {keys.map((k) => {
          const p = props[k] || {};
          const isSecret = p["x-format"] === "password" || p.format === "password";
          const isObject = p.type === "object";
          const cur = values[k];
          return (
            <label key={k} className="flex flex-col text-[11px] text-muted">
              <span>
                {k}{required.has(k) ? <span className="text-bad"> *</span> : null}
                {p.description && (
                  <span className="ml-1 text-muted">— {p.description}</span>
                )}
              </span>
              {isObject ? (
                <textarea
                  className="input min-h-[60px] font-mono text-xs"
                  placeholder="{ }"
                  value={typeof cur === "string" ? cur : (cur ? JSON.stringify(cur, null, 2) : "")}
                  onChange={(e) => {
                    const txt = e.target.value;
                    try {
                      onChange({ ...values, [k]: txt ? JSON.parse(txt) : undefined });
                    } catch {
                      // keep raw text so the user can fix typos before submit
                      onChange({ ...values, [k]: txt });
                    }
                  }}
                />
              ) : (
                <input
                  className="input"
                  type={isSecret ? "password" : "text"}
                  autoComplete={isSecret ? "new-password" : "off"}
                  value={(cur as string) ?? ""}
                  onChange={(e) => onChange({ ...values, [k]: e.target.value })}
                />
              )}
            </label>
          );
        })}
      </div>
      {hint && <p className="mt-1 text-[10px] text-muted">{hint}</p>}
    </div>
  );
}
